import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { Language } from '@pullman/shared';
import type { TranslationKey, Translations } from '@pullman/shared';
import en from './locales/en.json';
import hy from './locales/hy.json';
import ru from './locales/ru.json';

const locales: Record<Language, Translations> = {
  en: en as Translations,
  hy: hy as Translations,
  ru: ru as Translations,
};

function detectLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith('hy')) return 'hy';
  if (lang.startsWith('ru')) return 'ru';
  return 'en';
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(detectLanguage);

  const t = useCallback(
    (key: TranslationKey): string => {
      return locales[language][key] ?? locales.en[key] ?? key;
    },
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
  return ctx;
}
